import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  buttonStyles,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
} from '@zt/ui';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { toMinor, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  BalancePaymentRow,
  BizimhesapDiffRow,
  CompanyLedger,
  LedgerEntryRow,
  LedgerImportPreview,
  LedgerOverview,
  LedgerReminderSettings,
} from '../lib/types';
import { BizimhesapStatementSection } from '../components/BizimhesapStatement';

/**
 * Bayi cari hesabı (F7-01, ADR-104): genel bakış (bakiye, vadesi geçen), bayi ekstresi, tahsilat/düzeltme, ters
 * kayıt ve açılış bakiyesi aktarımı. Kayıtlar silinmez; yanlış kayıt ters kayıtla kapatılır.
 */

const METHODS = ['bank_transfer', 'cash', 'cheque', 'note', 'card', 'other'] as const;

async function download(path: string, fallback: string) {
  try {
    const { blob, fileName } = await api.blob(path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName ?? fallback;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    if (!(e instanceof ApiError && e.code === 'step_up_required'))
      toast.error(e instanceof ApiError ? e.title : String(e));
  }
}

// ---------------------------------------------------------------------------
// Genel bakış
// ---------------------------------------------------------------------------

export function LedgerOverviewPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [q, setQ] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [importing, setImporting] = useState(false);
  const params = new URLSearchParams();
  if (q.trim()) params.set('q', q.trim());
  if (onlyOverdue) params.set('onlyOverdue', 'true');
  const list = useQuery({
    queryKey: ['ledger', q.trim(), onlyOverdue],
    queryFn: () => api.get<LedgerOverview>(`/v1/admin/ledger?${params}`),
  });
  const d = list.data;
  return (
    <PageFrame
      helpKey="page.ledger"
      actions={
        can('b2b.ledger.manage') ? (
          <>
            <span className="inline-flex items-center gap-1">
              <Link to="/cari-hesaplar/banka-hareketleri" className={buttonStyles({ variant: 'secondary' })}>
                {t('admin.bankImport.open')}
              </Link>
              <HelpTip k="ledger.bank_import" />
            </span>
            <span className="inline-flex items-center gap-1">
              <Button onClick={() => setImporting(true)}>{t('admin.ledger.import')}</Button>
              <HelpTip k="ledger.import" />
            </span>
          </>
        ) : undefined
      }
    >
      {d && d.totals.length > 0 && (
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" aria-label={t('admin.ledger.totals')}>
          {d.totals.map((x) => (
            <div key={x.currency} className="rounded-md border border-line bg-surface p-4">
              <dt className="text-sm text-ink-3">
                {t('admin.ledger.totalBalance', { currency: x.currency })}
              </dt>
              <dd className="mt-1 text-xl font-semibold tabular">{money(x.balanceMinor, x.currency)}</dd>
              <dd className="mt-1 text-sm text-rose tabular">
                {t('admin.ledger.totalOverdue', { amount: money(x.overdueMinor, x.currency) })}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <div className="flex flex-wrap items-end gap-4">
        <Field label={t('admin.ledger.search')} help={<HelpTip k="ledger.search" />}>
          {(p) => <Input {...p} type="search" value={q} onChange={(e) => setQ(e.target.value)} />}
        </Field>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            label={t('admin.ledger.onlyOverdue')}
            checked={onlyOverdue}
            onCheckedChange={setOnlyOverdue}
          />
          <span className="text-sm">{t('admin.ledger.onlyOverdue')}</span>
          <HelpTip k="ledger.only_overdue" />
        </div>
      </div>
      {!d ? (
        <Spinner label={t('common.loading')} />
      ) : !d.items.length ? (
        <EmptyState title={t('admin.ledger.empty')} description={t('admin.ledger.emptyLead')} />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[46rem] text-sm">
            <caption className="sr-only">{t('admin.ledger.listCaption')}</caption>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.dealer')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.balance')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.overdue')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.notDue')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.oldestOverdue')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.credit.limitColumn')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.lastEntry')}</th>
              </tr>
            </thead>
            <tbody>
              {d.items.map((i) => (
                <tr key={`${i.companyId}:${i.currency}`} className="border-t border-line">
                  <td className="px-3 py-2">
                    <Link
                      to="/cari-hesaplar/$id"
                      params={{ id: i.companyId }}
                      className="font-medium text-pine underline-offset-2 hover:underline"
                    >
                      {i.legalName}
                    </Link>
                    {i.externalCode && <span className="block text-ink-3">{i.externalCode}</span>}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(i.balanceMinor, i.currency)}</td>
                  <td
                    className={`px-3 py-2 text-end tabular ${i.overdueMinor > 0 ? 'font-medium text-rose' : ''}`}
                  >
                    {money(i.overdueMinor, i.currency)}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(i.notDueMinor, i.currency)}</td>
                  <td className="px-3 py-2">
                    {i.oldestOverdueDate
                      ? t('admin.ledger.daysOverdue', {
                          date: formatDate(i.oldestOverdueDate, false),
                          days: i.oldestOverdueDays ?? 0,
                        })
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-end tabular">
                    {i.limitMinor === null ? (
                      '—'
                    ) : (
                      <>
                        {money(i.limitMinor, i.currency)}
                        <span className="block text-ink-3">
                          {money(Math.max(0, i.limitMinor - i.balanceMinor), i.currency)}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2">{i.lastEntryDate ? formatDate(i.lastEntryDate, false) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <BizimhesapBalancesSection />
      <ReminderSection />
      {importing && <ImportDialog onClose={() => setImporting(false)} />}
    </PageFrame>
  );
}

// ---------------------------------------------------------------------------
// BizimHesap bakiyeleri ve fark raporu (F7-25, ADR-113) — cari hesabın ana kaydı BizimHesap
// ---------------------------------------------------------------------------

function BizimhesapBalancesSection() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can, dealerScoped } = usePermissions();
  const q = useQuery({
    queryKey: ['bizimhesap-diff'],
    queryFn: () => api.get<BizimhesapDiffRow[]>('/v1/admin/bizimhesap/diff'),
  });
  const refresh = useApiMutation(
    () => api.post<{ queued: number }>('/v1/admin/bizimhesap/statements/refresh', {}),
    {
      invalidate: [['bizimhesap-diff']],
      success: (r) => t('admin.bizimhesap.diff.queued', { count: (r as { queued: number }).queued }),
    },
  );
  if (!q.data?.length) return null;
  return (
    <Section
      title={t('admin.bizimhesap.diff.title')}
      description={t('admin.bizimhesap.diff.lead')}
      action={
        can('b2b.ledger.manage') && !dealerScoped ? (
          <span className="inline-flex items-center gap-1">
            <Button size="sm" loading={refresh.isPending} onClick={() => refresh.mutate(undefined)}>
              {t('admin.bizimhesap.diff.refreshAll')}
            </Button>
            <HelpTip k="bizimhesap.refresh_all" />
          </span>
        ) : (
          <HelpTip k="bizimhesap.diff" />
        )
      }
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[50rem] text-sm" aria-label={t('admin.bizimhesap.diff.title')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.dealer')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.diff.bizimhesap')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.diff.pending')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.diff.ledger')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.diff.diff')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.diff.updated')}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((r) => (
              <tr key={r.companyId} className="border-t border-line">
                <td className="px-3 py-2">
                  <Link
                    to="/cari-hesaplar/$id"
                    params={{ id: r.companyId }}
                    className="font-medium text-pine underline-offset-2 hover:underline"
                  >
                    {r.legalName}
                  </Link>
                  <span className="block text-ink-3">{r.customerCode}</span>
                </td>
                <td className="px-3 py-2 text-end tabular">
                  {r.bizimhesapMinor === null ? '—' : money(r.bizimhesapMinor, r.currency)}
                </td>
                <td className="px-3 py-2 text-end tabular">{money(r.pendingMinor, r.currency)}</td>
                <td className="px-3 py-2 text-end tabular">{money(r.ledgerMinor, r.currency)}</td>
                <td className={`px-3 py-2 text-end tabular ${r.diffMinor ? 'font-medium text-rose' : ''}`}>
                  {r.diffMinor === null ? '—' : money(r.diffMinor, r.currency)}
                </td>
                <td className="px-3 py-2">
                  {r.fetchedAt ? formatDate(r.fetchedAt, true) : t('admin.bizimhesap.statement.neverFetched')}
                  {r.error && (
                    <span className="block text-xs text-rose">
                      {t('admin.bizimhesap.statement.lastError', { error: r.error })}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Vade hatırlatması (F7-03, ADR-106) — varsayılan kapalı; açmak gerçek bayilere e-posta gönderir
// ---------------------------------------------------------------------------

function ReminderSection() {
  const q = useQuery({
    queryKey: ['ledger-reminders'],
    queryFn: () => api.get<LedgerReminderSettings>('/v1/admin/ledger/settings'),
  });
  if (!q.data) return null;
  return <ReminderForm key={`${q.data.enabled}:${q.data.daysBefore}`} data={q.data} />;
}

function ReminderForm({ data }: { data: LedgerReminderSettings }) {
  const t = useT();
  const { can } = usePermissions();
  const manage = can('b2b.ledger.manage');
  const [enabled, setEnabled] = useState(data.enabled);
  const [days, setDays] = useState(String(data.daysBefore));
  const [ack, setAck] = useState(false);
  const turningOn = enabled && !data.enabled;
  const daysNum = Number(days);
  const daysValid = /^\d{1,2}$/.test(days) && daysNum <= 30;
  const changed = enabled !== data.enabled || daysNum !== data.daysBefore;
  const save = useApiMutation(
    () =>
      api.put<LedgerReminderSettings>('/v1/admin/ledger/settings', {
        enabled,
        daysBefore: daysNum,
        ...(turningOn ? { confirmSend: ack } : {}),
      }),
    {
      invalidate: [['ledger-reminders']],
      success: t('admin.ledger.reminders.saved'),
      silentFieldErrors: true,
    },
  );
  return (
    <Section
      title={t('admin.ledger.reminders.title')}
      description={t('admin.ledger.reminders.lead')}
      action={<HelpTip k="ledger.reminders" />}
    >
      {data.mailDelivery !== 'email' && (
        <p role="status" className="mb-3 rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
          {t('admin.ledger.reminders.noMail')}
        </p>
      )}
      <div className="flex flex-wrap items-end gap-6">
        <div className="flex items-center gap-2 pb-2">
          <Switch
            label={t('admin.ledger.reminders.enabled')}
            checked={enabled}
            disabled={!manage}
            onCheckedChange={(v) => {
              setEnabled(v);
              setAck(false);
            }}
          />
          <span className="text-sm">{t('admin.ledger.reminders.enabled')}</span>
          <HelpTip k="ledger.reminders_enabled" />
        </div>
        <Field
          label={t('admin.ledger.reminders.daysBefore')}
          help={<HelpTip k="ledger.reminders_days" />}
          error={daysValid ? fieldError(save.error, 'daysBefore') : t('admin.ledger.reminders.daysInvalid')}
        >
          {(p) => (
            <Input
              {...p}
              inputMode="numeric"
              className="w-24"
              value={days}
              disabled={!manage}
              onChange={(e) => setDays(e.target.value.trim())}
            />
          )}
        </Field>
      </div>
      {turningOn && (
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--zt-pine)]"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <span>{t('admin.ledger.reminders.confirm')}</span>
        </label>
      )}
      {fieldError(save.error, 'confirmSend') && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {fieldError(save.error, 'confirmSend')}
        </p>
      )}
      {manage && (
        <div className="mt-4 flex items-center gap-1">
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!changed || !daysValid || (turningOn && !ack)}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
          <HelpTip k="ledger.reminders_save" />
        </div>
      )}
    </Section>
  );
}

function ImportDialog({ onClose }: { onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<LedgerImportPreview | null>(null);
  const [idem] = useState(() => new IdempotencyKey());
  const upload = useApiMutation(
    (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.upload<LedgerImportPreview>('/v1/admin/ledger/imports', fd);
    },
    { onSuccess: (r) => setPreview(r) },
  );
  const apply = useApiMutation(
    (id: string) =>
      api.post<{ applied: number }>(`/v1/admin/ledger/imports/${id}/apply`, undefined, {
        idempotencyKey: idem,
      }),
    {
      invalidate: [['ledger'], ['company-ledger']],
      success: (r) => t('admin.ledger.importApplied', { count: r.applied }),
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open
      variant="sheet"
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.ledger.import')}
      description={t('admin.ledger.importLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          {preview && (
            <span className="inline-flex items-center gap-1">
              <Button
                variant="primary"
                disabled={!preview.summary.valid}
                loading={apply.isPending}
                onClick={() => apply.mutate(preview.id)}
              >
                {t('admin.ledger.importApply', { count: preview.summary.valid })}
              </Button>
              <HelpTip k="ledger.import_apply" />
            </span>
          )}
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <p className="text-ink-2">{t('admin.ledger.importColumns')}</p>
        <Field label={t('admin.ledger.importFile')} help={<HelpTip k="ledger.import" />}>
          {(p) => (
            <input
              {...p}
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="block text-sm"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setPreview(null);
                if (f) upload.mutate(f);
              }}
            />
          )}
        </Field>
        {upload.isPending && <Spinner label={t('common.loading')} />}
        {preview && (
          <>
            <p role="status" className="font-medium">
              {t('admin.ledger.importSummary', {
                valid: preview.summary.valid,
                invalid: preview.summary.invalid,
                totals:
                  Object.entries(preview.summary.totals)
                    .map(([cur, v]) => money(v, cur))
                    .join(' + ') || '—',
              })}
            </p>
            {preview.alreadyHasOpening > 0 && (
              <p role="alert" className="rounded-sm border border-amber/30 bg-amber-tint p-2">
                {t('admin.ledger.importAlready', { count: preview.alreadyHasOpening })}
              </p>
            )}
            <div className="max-h-[22rem] overflow-auto rounded-md border border-line">
              <table className="w-full text-sm">
                <caption className="sr-only">{t('admin.ledger.importPreview')}</caption>
                <thead className="sticky top-0 bg-surface-2 text-ink-2">
                  <tr>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.ledger.importLine')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.ledger.dealer')}</th>
                    <th className="px-3 py-1.5 text-end font-medium">{t('admin.ledger.amount')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.ledger.date')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.ledger.dueDate')}</th>
                    <th className="px-3 py-1.5 text-start font-medium">{t('admin.ledger.importStatus')}</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => (
                    <tr key={r.line} className="border-t border-line align-top">
                      <td className="px-3 py-1.5 tabular">{r.line}</td>
                      <td className="px-3 py-1.5">
                        {r.companyName ?? '—'}
                        <span className="block text-ink-3">{r.key}</span>
                      </td>
                      <td className="px-3 py-1.5 text-end tabular">
                        {r.errors.includes('amount_invalid') ? '—' : money(r.amountMinor, r.currency)}
                      </td>
                      <td className="px-3 py-1.5">{formatDate(r.entryDate, false)}</td>
                      <td className="px-3 py-1.5">{r.dueDate ? formatDate(r.dueDate, false) : '—'}</td>
                      <td className="px-3 py-1.5">
                        {r.errors.length ? (
                          <span className="text-rose">
                            {r.errors.map((e) => t(`admin.ledger.importErrors.${e}`)).join(' ')}
                          </span>
                        ) : (
                          <Badge tone="moss">{t('admin.ledger.importOk')}</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Bayi ekstresi
// ---------------------------------------------------------------------------

export function CompanyLedgerPage() {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { id } = useParams({ from: '/app/cari-hesaplar/$id' });
  const [currency, setCurrency] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [dialog, setDialog] = useState<'payment' | 'adjustment' | null>(null);
  const [reversing, setReversing] = useState<LedgerEntryRow | null>(null);
  const qs = new URLSearchParams();
  if (currency) qs.set('currency', currency);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const q = useQuery({
    queryKey: ['company-ledger', id, currency, from, to],
    queryFn: () => api.get<CompanyLedger>(`/v1/admin/companies/${id}/ledger?${qs}`),
  });
  const d = q.data;
  const manage = can('b2b.ledger.manage');
  if (!d) return <Spinner label={t('common.loading')} />;
  const cur = d.currencies.find((c) => c.currency === d.currency);
  return (
    <PageFrame
      helpKey="page.company_ledger"
      title={t('admin.ledger.companyTitle', { name: d.company.legalName })}
      actions={
        <div className="flex flex-wrap items-center gap-2 print:hidden">
          <Link to="/bayiler/$id" params={{ id }} className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.ledger.toDealer')}
          </Link>
          {manage && (
            <>
              <span className="inline-flex items-center gap-1">
                <Button variant="primary" onClick={() => setDialog('payment')}>
                  {t('admin.ledger.payment')}
                </Button>
                <HelpTip k="ledger.payment" />
              </span>
              <span className="inline-flex items-center gap-1">
                <Button onClick={() => setDialog('adjustment')}>{t('admin.ledger.adjustment')}</Button>
                <HelpTip k="ledger.adjustment" />
              </span>
            </>
          )}
        </div>
      }
    >
      <p className="text-sm text-ink-2">
        {t('admin.ledger.term', {
          days: d.company.paymentTermDays ?? 0,
        })}
        {d.credit.limitMinor !== null && (
          <span className="block">
            {t('admin.credit.summary', {
              limit: money(d.credit.limitMinor, d.credit.currency),
              available: money(d.credit.availableMinor ?? 0, d.credit.currency),
            })}
          </span>
        )}
        {d.credit.blocked && (
          <span className="block font-medium text-rose">{t(`admin.credit.blocked.${d.credit.blocked}`)}</span>
        )}
        {d.credit.limitMinor !== null && d.credit.source === 'bizimhesap' && (
          <span className="block text-ink-3">{t('admin.bizimhesap.statement.creditSource')}</span>
        )}
      </p>
      <BizimhesapStatementSection companyId={id} />
      <BalancePaymentsSection companyId={id} />
      {cur ? (
        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Summary label={t('admin.ledger.balance')} value={money(cur.balanceMinor, cur.currency)} strong />
          <Summary
            label={t('admin.ledger.overdue')}
            value={money(cur.overdueMinor, cur.currency)}
            tone={cur.overdueMinor > 0 ? 'rose' : undefined}
          />
          <Summary label={t('admin.ledger.notDue')} value={money(cur.notDueMinor, cur.currency)} />
          <Summary
            label={t('admin.ledger.oldestOverdue')}
            value={
              cur.oldestOverdueDate
                ? t('admin.ledger.daysOverdue', {
                    date: formatDate(cur.oldestOverdueDate, false),
                    days: cur.oldestOverdueDays ?? 0,
                  })
                : '—'
            }
          />
        </dl>
      ) : (
        <EmptyState title={t('admin.ledger.noEntries')} description={t('admin.ledger.noEntriesLead')} />
      )}
      {d.openItems.length > 0 && (
        <Section
          title={t('admin.ledger.openItems')}
          description={t('admin.ledger.openItemsLead')}
          action={<HelpTip k="ledger.open_items" />}
        >
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[36rem] text-sm">
              <caption className="sr-only">{t('admin.ledger.openItems')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.openItem')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.dueDate')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.original')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.open')}</th>
                </tr>
              </thead>
              <tbody>
                {d.openItems.map((i) => (
                  <tr key={i.key} className="border-t border-line">
                    <td className="px-3 py-2">
                      {i.orderId ? (
                        <Link
                          to="/siparisler/$id"
                          params={{ id: i.orderId }}
                          className="text-pine underline underline-offset-2"
                        >
                          {i.orderNumber ?? i.orderId}
                        </Link>
                      ) : (
                        <>
                          {t(`admin.ledger.kinds.${i.entryKind ?? 'opening'}`)}
                          {i.description && <span className="block text-ink-3">{i.description}</span>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 tabular">
                      {i.overdueDays !== null ? (
                        <span className="font-medium text-rose">
                          {t('admin.ledger.daysOverdue', {
                            date: formatDate(i.dueDate, false),
                            days: i.overdueDays,
                          })}
                        </span>
                      ) : (
                        formatDate(i.dueDate, false)
                      )}
                    </td>
                    <td className="px-3 py-2 text-end tabular">{money(i.originalMinor, d.currency)}</td>
                    <td className="px-3 py-2 text-end tabular font-medium">
                      {money(i.openMinor, d.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
      <Section
        title={t('admin.ledger.statement')}
        action={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <span className="inline-flex items-center gap-1">
              <Button
                size="sm"
                onClick={() => download(`/v1/admin/companies/${id}/ledger/statement.csv?${qs}`, 'cari.csv')}
              >
                {t('admin.ledger.csv')}
              </Button>
              <HelpTip k="ledger.statement_csv" />
            </span>
            <span className="inline-flex items-center gap-1">
              <Button size="sm" onClick={() => window.print()}>
                {t('admin.ledger.print')}
              </Button>
              <HelpTip k="ledger.print" />
            </span>
          </div>
        }
      >
        <div className="mb-3 flex flex-wrap items-end gap-4 print:hidden">
          {d.currencies.length > 1 && (
            <Field label={t('admin.ledger.currency')} help={<HelpTip k="ledger.currency" />}>
              {(p) => (
                <Select {...p} value={d.currency} onChange={(e) => setCurrency(e.target.value)}>
                  {d.currencies.map((c) => (
                    <option key={c.currency} value={c.currency}>
                      {c.currency}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <Field label={t('admin.ledger.from')} help={<HelpTip k="ledger.period" />}>
            {(p) => <Input {...p} type="date" value={from} onChange={(e) => setFrom(e.target.value)} />}
          </Field>
          <Field label={t('admin.ledger.to')}>
            {(p) => <Input {...p} type="date" value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
        </div>
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[52rem] text-sm">
            <caption className="sr-only">{t('admin.ledger.statement')}</caption>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.date')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.kind')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.description')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.dueDate')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.debit')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.credit')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.balance')}</th>
                <th className="px-3 py-2 print:hidden">
                  <span className="sr-only">{t('admin.ledger.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {d.from && (
                <tr className="border-t border-line bg-surface-2/50">
                  <td className="px-3 py-2">{formatDate(d.from, false)}</td>
                  <td className="px-3 py-2" colSpan={5}>
                    {t('admin.ledger.broughtForward')}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(d.openingMinor, d.currency)}</td>
                  <td className="print:hidden" />
                </tr>
              )}
              {d.entries.map((e) => (
                <tr key={e.id} className="border-t border-line align-top">
                  <td className="px-3 py-2 tabular">{formatDate(e.entryDate, false)}</td>
                  <td className="px-3 py-2">
                    {t(`admin.ledger.kinds.${e.kind}`)}
                    {e.reversed && (
                      <Badge tone="slate" className="ms-2">
                        {t('admin.ledger.reversedBadge')}
                      </Badge>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {e.orderNumber && e.orderId && (
                      <Link
                        to="/siparisler/$id"
                        params={{ id: e.orderId }}
                        className="me-1 text-pine underline underline-offset-2"
                      >
                        {e.orderNumber}
                      </Link>
                    )}
                    {[
                      e.reason ? t(`admin.ledger.reasons.${e.reason}`) : null,
                      e.method ? t(`admin.ledger.methods.${e.method}`) : null,
                      e.reference,
                      e.description,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                    {e.bizimhesap && (
                      <span className="mt-1 block text-xs text-ink-3">
                        {t('admin.bizimhesap.ledgerEntry', {
                          number: e.bizimhesap.number,
                          status: t(`admin.bizimhesap.statuses.${e.bizimhesap.status as 'pending'}`),
                        })}
                        {e.bizimhesap.note && ` · ${e.bizimhesap.note}`}
                        {e.bizimhesap.ledgerReversed && e.bizimhesap.status !== 'cancelled' && (
                          <span className="block text-rose">{t('admin.bizimhesap.reversedFixAtBh')}</span>
                        )}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular">{e.dueDate ? formatDate(e.dueDate, false) : '—'}</td>
                  <td className="px-3 py-2 text-end tabular">
                    {e.amountMinor > 0 ? money(e.amountMinor, d.currency) : ''}
                  </td>
                  <td className="px-3 py-2 text-end tabular">
                    {e.amountMinor < 0 ? money(-e.amountMinor, d.currency) : ''}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(e.runningMinor, d.currency)}</td>
                  <td className="px-3 py-2 text-end print:hidden">
                    {manage && e.reversible && (
                      <span className="inline-flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setReversing(e)}>
                          {t('admin.ledger.reverse')}
                        </Button>
                        <HelpTip k="ledger.reverse" />
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {!d.entries.length && !d.from && (
                <tr className="border-t border-line">
                  <td className="px-3 py-3 text-ink-3" colSpan={8}>
                    {t('admin.ledger.noEntries')}
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="border-t-2 border-line font-medium">
              <tr>
                <td className="px-3 py-2" colSpan={4}>
                  {t('admin.ledger.periodTotal')}
                </td>
                <td className="px-3 py-2 text-end tabular">{money(d.debitMinor, d.currency)}</td>
                <td className="px-3 py-2 text-end tabular">{money(d.creditMinor, d.currency)}</td>
                <td className="px-3 py-2 text-end tabular">{money(d.closingMinor, d.currency)}</td>
                <td className="print:hidden" />
              </tr>
            </tfoot>
          </table>
        </div>
      </Section>
      {dialog && (
        <EntryDialog kind={dialog} companyId={id} currency={d.currency} onClose={() => setDialog(null)} />
      )}
      {reversing && <ReverseDialog entry={reversing} onClose={() => setReversing(null)} />}
    </PageFrame>
  );
}

/**
 * Bayinin Hesabım'dan kartla yaptığı bakiye ödemeleri (F7-23, ADR-114). Alınan ödeme deftere kendiliğinden yazılır;
 * "İnceleniyor" durumundaki ödeme deftere yazılmaz, sağlayıcı panelinden kontrol edilir.
 */
function BalancePaymentsSection({ companyId }: { companyId: string }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['company-balance-payments', companyId],
    queryFn: () => api.get<BalancePaymentRow[]>(`/v1/admin/companies/${companyId}/balance-payments`),
  });
  if (!q.data?.length) return null;
  const tone = {
    captured: 'moss',
    pending: 'neutral',
    failed: 'rose',
    expired: 'slate',
    requires_action: 'amber',
  } as const;
  return (
    <Section
      title={t('admin.balancePayments.title')}
      description={t('admin.balancePayments.lead')}
      action={<HelpTip k="ledger.balance_payments" />}
    >
      <div className="overflow-x-auto rounded-md border border-line">
        <table className="w-full min-w-[40rem] text-sm" aria-label={t('admin.balancePayments.title')}>
          <thead className="bg-surface-2 text-ink-2">
            <tr>
              <th className="px-3 py-2 text-start font-medium">{t('admin.ledger.date')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.balancePayments.reference')}</th>
              <th className="px-3 py-2 text-end font-medium">{t('admin.ledger.amount')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.balancePayments.status')}</th>
              <th className="px-3 py-2 text-start font-medium">{t('admin.balancePayments.card')}</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((p) => (
              <tr key={p.id} className="border-t border-line align-top">
                <td className="px-3 py-2">{formatDate(p.createdAt, true)}</td>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs">{p.reference}</span>
                  <span className="block text-xs text-ink-3">
                    {t(`admin.balancePayments.modes.${p.mode}`)} ·{' '}
                    {t(`admin.balancePayments.providers.${p.provider}`)}
                    {p.environment === 'sandbox' && ` · ${t('admin.balancePayments.sandbox')}`}
                  </span>
                </td>
                <td className="px-3 py-2 text-end tabular">{money(p.amountMinor, p.currency)}</td>
                <td className="px-3 py-2">
                  <Badge tone={tone[p.status]}>{t(`admin.balancePayments.statuses.${p.status}`)}</Badge>
                  {p.status === 'requires_action' && (
                    <span className="mt-1 block text-xs text-ink-2">
                      {t('admin.balancePayments.reviewHint', {
                        reason: t(`admin.balancePayments.reasons.${(p.failureCode ?? 'other') as 'other'}`),
                      })}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {p.cardLastFour ? `•••• ${p.cardLastFour}` : '—'}
                  {p.installmentCount && p.installmentCount > 1 && (
                    <span className="block text-xs text-ink-3">
                      {t('admin.balancePayments.installments', { count: p.installmentCount })}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

function Summary({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'rose';
}) {
  return (
    <div className="rounded-md border border-line bg-surface p-4">
      <dt className="text-sm text-ink-3">{label}</dt>
      <dd
        className={`mt-1 tabular ${strong ? 'text-xl font-semibold' : 'text-lg'} ${tone === 'rose' ? 'text-rose' : ''}`}
      >
        {value}
      </dd>
    </div>
  );
}

function EntryDialog({
  kind,
  companyId,
  currency,
  onClose,
}: {
  kind: 'payment' | 'adjustment';
  companyId: string;
  currency: string;
  onClose: () => void;
}) {
  const t = useT();
  const [amount, setAmount] = useState('');
  const [entryDate, setEntryDate] = useState('');
  const [method, setMethod] = useState<(typeof METHODS)[number]>('bank_transfer');
  const [reference, setReference] = useState('');
  const [direction, setDirection] = useState<'debit' | 'credit'>('debit');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const amountMinor = toMinor(amount, currency);
  const body = {
    kind,
    currency,
    amountMinor,
    ...(entryDate ? { entryDate } : {}),
    ...(kind === 'payment'
      ? { method, reference: reference.trim() || null }
      : { direction, dueDate: direction === 'debit' && dueDate ? dueDate : null }),
    description: description.trim() || null,
  };
  const save = useApiMutation(
    () =>
      api.post(`/v1/admin/companies/${companyId}/ledger/entries`, body, {
        idempotencyKey: idem,
      }),
    {
      invalidate: [['company-ledger', companyId], ['ledger']],
      success: t(kind === 'payment' ? 'admin.ledger.paymentSaved' : 'admin.ledger.adjustmentSaved'),
      silentFieldErrors: true,
      onSuccess: onClose,
    },
  );
  const err = (f: string) => fieldError(save.error, f);
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t(kind === 'payment' ? 'admin.ledger.payment' : 'admin.ledger.adjustment')}
      description={t(kind === 'payment' ? 'admin.ledger.paymentLead' : 'admin.ledger.adjustmentLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!amountMinor || amountMinor <= 0 || (kind === 'adjustment' && !description.trim())}
            onClick={() => save.mutate(undefined)}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {kind === 'adjustment' && (
          <Field label={t('admin.ledger.direction')} help={<HelpTip k="ledger.direction" />}>
            {(p) => (
              <Select
                {...p}
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'debit' | 'credit')}
              >
                <option value="debit">{t('admin.ledger.directions.debit')}</option>
                <option value="credit">{t('admin.ledger.directions.credit')}</option>
              </Select>
            )}
          </Field>
        )}
        <Field
          label={t('admin.ledger.amountIn', { currency })}
          help={<HelpTip k="ledger.amount" />}
          error={
            err('amountMinor') ??
            (amount && amountMinor === null ? t('admin.ledger.amountInvalid') : undefined)
          }
        >
          {(p) => (
            <Input
              {...p}
              inputMode="decimal"
              className="tabular"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('admin.ledger.entryDate')}
          help={<HelpTip k="ledger.entry_date" />}
          optionalLabel={t('common.optional')}
          error={err('entryDate')}
        >
          {(p) => (
            <Input {...p} type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
          )}
        </Field>
        {kind === 'payment' ? (
          <>
            <Field
              label={t('admin.ledger.method')}
              help={<HelpTip k="ledger.method" />}
              error={err('method')}
            >
              {(p) => (
                <Select
                  {...p}
                  value={method}
                  onChange={(e) => setMethod(e.target.value as (typeof METHODS)[number])}
                >
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {t(`admin.ledger.methods.${m}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.ledger.reference')}
              help={<HelpTip k="ledger.reference" />}
              optionalLabel={t('common.optional')}
            >
              {(p) => (
                <Input
                  {...p}
                  maxLength={120}
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              )}
            </Field>
          </>
        ) : (
          direction === 'debit' && (
            <Field
              label={t('admin.ledger.dueDate')}
              help={<HelpTip k="ledger.due_date" />}
              optionalLabel={t('common.optional')}
            >
              {(p) => (
                <Input {...p} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
              )}
            </Field>
          )
        )}
        <div className="sm:col-span-2">
          <Field
            label={t('admin.ledger.description')}
            optionalLabel={kind === 'payment' ? t('common.optional') : undefined}
            error={err('description')}
          >
            {(p) => (
              <Input
                {...p}
                maxLength={500}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            )}
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

function ReverseDialog({ entry, onClose }: { entry: LedgerEntryRow; onClose: () => void }) {
  const t = useT();
  const money = useMoney();
  const [reason, setReason] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const m = useApiMutation(
    () =>
      api.post(
        `/v1/admin/ledger/entries/${entry.id}/reverse`,
        { reason: reason.trim() },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['company-ledger'], ['ledger']],
      success: t('admin.ledger.reversed'),
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.ledger.reverseTitle')}
      description={t('admin.ledger.reverseLead', {
        kind: t(`admin.ledger.kinds.${entry.kind}`),
        amount: money(Math.abs(entry.amountMinor), entry.currency),
      })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            loading={m.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.ledger.reverse')}
          </Button>
        </>
      }
    >
      <Field label={t('admin.ledger.reverseReason')}>
        {(p) => <Input {...p} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
      </Field>
    </Dialog>
  );
}
